import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { TagIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import TenantProductPricesTab from './TenantProductPricesTab';
import TenantDomainPricesTab from './TenantDomainPricesTab';

type PricesTabKey = 'products' | 'domains';

/**
 * Prices — a read-only view of the in-scope client's effective prices (their own, or the impersonated
 * client; the /prices and /prices/domains routes resolve via the impersonation header). Two tabs:
 * Products (scalar) and Domains (the operation × TLD matrix). Editing now lives in the staff-only
 * Admin → Products and Prices / Domain Pricing pages, so this tenant page is purely display-only.
 */
function Prices() {
  const auth = useAuth();
  const [activeTabKey, setActiveTabKey] = useState<PricesTabKey>('products');

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Organization</BreadcrumbItem>
          <BreadcrumbItem isActive>Prices</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <TagIcon />
          </Icon>
          Prices
        </Content>
        <Content component="p">
          Effective prices for your organization&apos;s products and domain operations, excluding VAT.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTabKey}
          onSelect={(_event, tabKey) => setActiveTabKey(tabKey as PricesTabKey)}
          mountOnEnter
          aria-label="Price sections"
        >
          <Tab eventKey="products" title={<TabTitleText>Products</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <TenantProductPricesTab />
            </div>
          </Tab>
          <Tab eventKey="domains" title={<TabTitleText>Domains</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <TenantDomainPricesTab />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export default Prices;
